// Live's Presets tab (#264, spec docs/specs/editor/captions.md): the caption presets as 3:1 cards
// in one column, the member's own first (part 3) and live's six system presets, a check on the one the
// look was last picked from. A click puts the preset's look on every caption and re-cuts them by
// its paragraph settings from their own words ("Applied {name} style"). A card shows the preset's
// own look on a sample line (decision 18: live's loop videos from its CDN). The member's own preset
// (part 3) shows "Saved" and, on hover, "Delete preset", unless it is the one picked.
import { ActionIcon, Skeleton, Text, Tooltip } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { Check, Trash2 } from "lucide-react";
import { useEffect, useRef, useState, type CSSProperties } from "react";

import {
  getListCaptionPresetsQueryKey,
  useDeleteCaptionPreset,
  useListCaptionPresets,
} from "../../../api/generated/endpoints/editor/editor";
import type { CaptionPreset } from "../../../api/generated/model";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { toUserMessage } from "../../../platform/messages";
import { faceName, loadFaces } from "../fonts";
import type { AspectRatio } from "../project";
import { shownText } from "../text-style";
import { newId, spokenWords, type Timeline, type TimelineEdit } from "../timeline";
import { fromWireCaptions } from "../wire";
import { wordColor, type CaptionSettings, type WordState } from "./caption-style";
import { captionCut, cutWith } from "./recut";
import { MAX_CAPTIONS } from "./words";
import type { Said } from "./Transcript";
import classes from "./captions.module.css";

export interface PresetsTabProps {
  teamId: string;
  timeline: Timeline;
  aspectRatio: AspectRatio;
  readOnly: boolean;
  onEdit: (edit: TimelineEdit) => void;
  onSay: (said: Said | null) => void;
}

/** A preset's look as a project takes it: its settings, the project's letter and line spacing
 * kept (live's click leaves them), and the preset named. */
export function presetLook(preset: CaptionPreset, current: CaptionSettings): CaptionSettings {
  const settings = fromWireCaptions(preset.settings);
  return {
    ...settings,
    style: {
      ...settings.style,
      letterSpacing: current.style.letterSpacing,
      lineHeight: current.style.lineHeight,
    },
    presetId: preset.id,
  };
}

export function PresetsTab(props: PresetsTabProps) {
  const presets = useListCaptionPresets(props.teamId);
  const [applying, setApplying] = useState(false);
  const queryClient = useQueryClient();
  // Live's bin: no question, then "Deleted {name}".
  const remove = useDeleteCaptionPreset({
    mutation: {
      onSuccess: async (_, { presetId }) => {
        const name = presets.data?.find((preset) => preset.id === presetId)?.name ?? "";
        props.onSay({ tone: "success", text: `Deleted ${name}` });
        await queryClient.invalidateQueries({ queryKey: getListCaptionPresetsQueryKey(props.teamId) });
      },
      onError: (error) => props.onSay({ tone: "error", text: `Failed to delete preset: ${toUserMessage(error)}` }),
    },
  });
  const chosen = props.timeline.captions.presetId;
  // The timeline as it is now, for the checks after the face loads: the reducer cuts that one, so
  // the line said must be about it, not the timeline of the click.
  const latest = useRef(props.timeline);
  useEffect(() => {
    latest.current = props.timeline;
  });
  // The cards' faces, loaded with the tab; a card shows the fallback face until its own is in.
  useEffect(() => {
    if (presets.data === undefined) return;
    void loadFaces(
      presets.data.map((preset) => {
        const style = fromWireCaptions(preset.settings).style;
        return { family: style.font, weight: style.weight, italic: style.italic, text: SAMPLE.join(" ") };
      }),
    );
  }, [presets.data]);
  const apply = async (preset: CaptionPreset) => {
    if (props.readOnly || applying) return;
    const said = recutRefusal(props.timeline);
    if (said !== null) {
      props.onSay(said);
      return;
    }
    setApplying(true);
    try {
      const captions = presetLook(preset, props.timeline.captions);
      // The face measured first; the timeline then cuts its captions as they are by then, and
      // refuses as the checks below do (a caption locked or too many made meanwhile).
      const cut = await captionCut(captions, spokenWords(props.timeline), props.aspectRatio);
      const now = latest.current;
      const refused = recutRefusal(now);
      if (refused !== null) {
        props.onSay(refused);
        return;
      }
      const count = cutWith(spokenWords(now), captions, cut).length;
      if (count > MAX_CAPTIONS) {
        props.onSay({ tone: "error", text: `${preset.name} would make ${count} captions; a video holds ${MAX_CAPTIONS}.` });
        return;
      }
      props.onEdit({ type: "setCaptions", captions, cut, idPrefix: newId(), newTrackId: newId() });
      props.onSay({ tone: "success", text: `Applied ${preset.name} style` });
    } finally {
      setApplying(false);
    }
  };
  if (presets.isError) return <ErrorAlert error={presets.error} />;
  if (presets.data?.length === 0) return <Text className={classes.said}>No presets found</Text>;
  return (
    <ul className={classes.presets} aria-label="Caption presets" aria-busy={presets.isPending}>
      {presets.isPending
        ? [0, 1, 2, 3].map((at) => (
            <li key={at}>
              <Skeleton className={classes.presetFrame} />
              <Skeleton height={10} width="40%" mt={6} />
            </li>
          ))
        : presets.data.map((preset) => (
            <li key={preset.id} className={classes.presetItem}>
              <button
                type="button"
                className={classes.preset}
                aria-pressed={preset.id === chosen}
                aria-label={`${preset.name} style`}
                disabled={props.readOnly || applying}
                onClick={() => void apply(preset)}
              >
                <PresetPreview settings={fromWireCaptions(preset.settings)} />
                {preset.id === chosen ? (
                  <span className={classes.presetCheck} aria-hidden>
                    <Check size={10} />
                  </span>
                ) : null}
                {!preset.system && preset.id !== chosen ? <span className={classes.presetSaved}>Saved</span> : null}
              </button>
              {!preset.system && preset.id !== chosen ? (
                <Tooltip label="Delete preset">
                  <ActionIcon
                    variant="filled"
                    color="dark"
                    size={22}
                    className={classes.presetDelete}
                    aria-label={`Delete preset ${preset.name}`}
                    disabled={props.readOnly || remove.isPending}
                    onClick={() => remove.mutate({ teamId: props.teamId, presetId: preset.id })}
                  >
                    <Trash2 size={12} />
                  </ActionIcon>
                </Tooltip>
              ) : null}
              <Text className={classes.presetName}>{preset.name}</Text>
            </li>
          ))}
    </ul>
  );
}

/** The sample line a card shows, its second word being spoken. */
const SAMPLE = ["The", "quick", "brown", "fox"];
const STATES: readonly WordState[] = ["spoken", "active", "upcoming", "upcoming"];
/** A card's words at this share of the preset's size (live's previews are 900 by 300). */
const CARD_SCALE = 0.3;

/** A preset's look on the sample line, drawn by the page in its face and colours. */
function PresetPreview({ settings }: { settings: CaptionSettings }) {
  const { style } = settings;
  const background = style.background;
  const shadow = style.shadow;
  const line: CSSProperties = {
    fontFamily: `"${faceName(style.font)}", sans-serif`,
    fontWeight: style.weight,
    fontStyle: style.italic ? "italic" : "normal",
    fontSize: style.size * CARD_SCALE,
    ...(background === null
      ? {}
      : {
          background: `color-mix(in srgb, ${background.color} ${Math.round(background.opacity * 100)}%, transparent)`,
          borderRadius: background.radius * CARD_SCALE,
          padding: `${background.padY * CARD_SCALE}px ${background.padX * CARD_SCALE}px`,
        }),
    ...(style.stroke === null || style.stroke.width <= 0
      ? {}
      : { WebkitTextStroke: `${style.stroke.width * CARD_SCALE * 2}px ${style.stroke.color}`, paintOrder: "stroke fill" }),
    ...(shadow === null
      ? {}
      : {
          textShadow: `${shadow.offsetX * CARD_SCALE}px ${shadow.offsetY * CARD_SCALE}px ${shadow.blur * CARD_SCALE}px color-mix(in srgb, ${shadow.color} ${Math.round(shadow.alpha * 100)}%, transparent)`,
        }),
  };
  return (
    <span className={classes.presetFrame} aria-hidden>
      <span className={classes.presetLine} style={line}>
        {SAMPLE.map((word, at) => {
          const state = STATES[at]!;
          const active = state === "active" && style.activeBackground !== null;
          return (
            <span
              key={word}
              className={classes.presetWord}
              style={{
                color: wordColor(style, state) ?? "transparent",
                ...(active ? { background: style.activeBackground ?? undefined } : {}),
              }}
            >
              {shownText(word, style.textCase)}
            </span>
          );
        })}
      </span>
    </span>
  );
}

/** Why a re-cut cannot run now, in a line, or null: a locked caption keeps as it is. */
export function recutRefusal(timeline: Timeline): Said | null {
  const locked = timeline.clips.some((clip) => clip.kind === "caption" && clip.locked);
  return locked ? { tone: "error", text: "Unlock the captions to restyle them." } : null;
}
